import { type ActionItem, type ResponsibleParty } from '@consigli/types';

import { formatResponsibleParties } from './participant-utils';

export const stripMarkdown = (text: string): string =>
  text
    .replace(/^#{1,6}\s+/gm, '') // headings
    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
    .replace(/\*(.+?)\*/g, '$1') // italic
    .replace(/~~(.+?)~~/g, '$1') // strikethrough
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1') // inline/block code
    .replace(/\[(.+?)\]\(.+?\)/g, '$1') // links
    .replace(/^[-*+]\s+/gm, '• ') // unordered list bullets
    .replace(/^\d+\.\s+/gm, (m) => m) // ordered list (keep as-is)
    .replace(/^>\s+/gm, '') // blockquotes
    .replace(/[-*_]{3,}/g, '') // horizontal rules
    .trim();

/**
 * Formats grouped action items into a plain-text string suitable for copying to clipboard.
 *
 * @param groupedActions - A record mapping person/team names to their action items
 * @returns A formatted string with persons sorted alphabetically, each followed by their bullet-pointed action items
 */
export const formatActionItemsForCopy = (groupedActions: Record<string, ActionItem[]>): string => {
  return Object.entries(groupedActions)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([person, items]) => {
      const itemsList = items.map((item) => `  \u2022 ${item.descriptionText}`).join('\n');
      return `${person}:\n${itemsList}`;
    })
    .join('\n\n');
};

/**
 * Formats a meeting date string for display in copied text.
 */
const formatMeetingDateForCopy = (dateStr: string): string => {
  const normalized = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  return new Date(normalized).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Formats page numbers array into a readable string (e.g., "Page 1" or "Pages 1, 3, 5").
 */
const formatPageNumbersForCopy = (pageNumbers: number[]): string => {
  if (pageNumbers.length === 1) {
    return `Page ${pageNumbers[0]}`;
  }
  return `Pages ${pageNumbers.join(', ')}`;
};

/**
 * Formats contradictions into a plain-text string suitable for copying to clipboard.
 * Includes: header/title, reason, linked meeting agreements, document sources, and meeting source info.
 * Excludes: confidence scores.
 *
 * @param contradictions - Array of contradiction objects with their linked agreements and document sources
 * @param fallbackTitle - Title to use when contradiction has no title
 * @returns A formatted string with numbered contradictions
 */
const KEY_PREFIX = 'meeting-secretary.meetings';

export const formatContradictionsForCopy = (
  contradictions: Array<{
    title?: string;
    contradictionDescription?: string;
    agreementLinks?: Array<{
      agreementText?: string;
      contradictionReason?: string | null;
      meetingDatetime?: string | null;
      responsibleParties?: ResponsibleParty[] | null;
    }>;
    chunkLinks?: Array<{
      chunkText?: string;
      fileName?: string | null;
      pageNumbers?: number[] | null;
      contradictionReason?: string | null;
    }>;
  }>,
  fallbackTitle: string,
  t: (key: string) => string,
): string => {
  return contradictions
    .map((contradiction, index) => {
      // Header/Title
      let text = `${index + 1}. ${contradiction.title || fallbackTitle}`;

      // Reason/Description
      if (contradiction.contradictionDescription) {
        text += `\n   ${contradiction.contradictionDescription}`;
      }

      // Linked Meeting Agreements
      const links = contradiction.agreementLinks || [];
      if (links.length > 0) {
        text += `\n\n   ${t(`${KEY_PREFIX}.copy-linked-agreements`)}:\n`;
        links.forEach((link) => {
          if (link.agreementText) {
            text += `\n   • ${link.agreementText}`;
            if (link.meetingDatetime) {
              text += `\n     ${t(`${KEY_PREFIX}.copy-from-meeting`)}: ${formatMeetingDateForCopy(link.meetingDatetime)}`;
            }
            if (link.contradictionReason) {
              text += `\n     ${t(`${KEY_PREFIX}.copy-reason`)}: ${link.contradictionReason}`;
            }
          }
        });
      }

      // Document Sources (chunk links)
      const chunks = contradiction.chunkLinks || [];
      if (chunks.length > 0) {
        text += `\n\n   ${t(`${KEY_PREFIX}.copy-document-sources`)}:\n`;
        chunks.forEach((chunk, chunkIndex) => {
          // Add extra blank line between document sources
          if (chunkIndex > 0) {
            text += `\n`;
          }
          const sourceInfo: string[] = [];
          if (chunk.fileName) {
            sourceInfo.push(chunk.fileName);
          }
          if (chunk.pageNumbers && chunk.pageNumbers.length > 0) {
            sourceInfo.push(formatPageNumbersForCopy(chunk.pageNumbers));
          }
          if (sourceInfo.length > 0) {
            text += `\n   • ${sourceInfo.join(' - ')}`;
          }
          if (chunk.chunkText) {
            text += `\n     "${chunk.chunkText}"`;
          }
          if (chunk.contradictionReason) {
            text += `\n     ${t(`${KEY_PREFIX}.copy-reason`)}: ${chunk.contradictionReason}`;
          }
        });
      }

      return text;
    })
    .join('\n\n');
};

/**
 * Formats agreements into a plain-text string suitable for copying to clipboard.
 *
 * @param agreements - Array of meeting agreement objects
 * @returns A formatted string with numbered agreements
 */
export const formatAgreementsForCopy = (
  agreements: Array<{
    agreement: string;
    responsibleParties?: ResponsibleParty[] | null;
    active?: boolean | null;
  }>,
  t: (key: string) => string,
): string => {
  return agreements
    .map((agreement, index) => {
      let text = `${index + 1}. ${agreement.agreement}`;
      const parties = formatResponsibleParties(agreement.responsibleParties);
      if (parties) {
        text += `\n   ${t(`${KEY_PREFIX}.responsible-label`)}: ${parties}`;
      }
      if (agreement.active === false) {
        text += ` [${t(`${KEY_PREFIX}.inactive-label`)}]`;
      }
      return text;
    })
    .join('\n\n');
};

/**
 * Formats transcript entries into a plain-text string suitable for copying to clipboard.
 *
 * @param transcripts - Array of parsed transcript entries
 * @returns A formatted string with timestamped speaker entries
 */
export const formatTranscriptForCopy = (
  transcripts: Array<{ startTime: string; speaker: string; text: string }>,
): string => {
  return transcripts
    .map((entry) => `[${entry.startTime}] ${entry.speaker}: ${entry.text}`)
    .join('\n');
};

/**
 * Formats a meeting summary into a plain-text string suitable for copying to clipboard.
 *
 * @param summary - The meeting summary object
 * @returns The generated summary text
 */
export const formatSummaryForCopy = (
  summary: {
    generatedSummaryText?: string;
  } | null,
): string => {
  if (!summary) return '';
  if (!summary.generatedSummaryText) return '';
  return stripMarkdown(summary.generatedSummaryText);
};
